import React, { useState } from 'react';
import './Settings.css';

function Settings({ currentUser, onThemeChange, currentTheme }) {
  const [activeTab, setActiveTab] = useState('general');
  const [notifications, setNotifications] = useState({
    email: true,
    discord: false,
    giveawayUpdates: true,
    announcements: true,
    messages: true,
  });
  const [privacy, setPrivacy] = useState({
    showProfile: true,
    showEntries: true,
    allowMessages: true,
    showOnlineStatus: true,
  });

  const handleNotificationChange = (key) => {
    setNotifications(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handlePrivacyChange = (key) => {
    setPrivacy(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  return (
    <div className="settings-page">
      <div className="settings-container">
        <h1 className="settings-title">⚙️ Settings</h1>

        <div className="settings-layout">
          <div className="settings-sidebar">
            <button
              className={activeTab === 'general' ? 'active' : ''}
              onClick={() => setActiveTab('general')}
            >
              General
            </button>
            <button
              className={activeTab === 'appearance' ? 'active' : ''}
              onClick={() => setActiveTab('appearance')}
            >
              Appearance
            </button>
            <button
              className={activeTab === 'notifications' ? 'active' : ''}
              onClick={() => setActiveTab('notifications')}
            >
              Notifications
            </button>
            <button
              className={activeTab === 'privacy' ? 'active' : ''}
              onClick={() => setActiveTab('privacy')}
            >
              Privacy
            </button>
            <button
              className={activeTab === 'account' ? 'active' : ''}
              onClick={() => setActiveTab('account')}
            >
              Account
            </button>
            <button
              className={activeTab === 'advanced' ? 'active' : ''}
              onClick={() => setActiveTab('advanced')}
            >
              Advanced
            </button>
          </div>

          <div className="settings-content">
            {activeTab === 'general' && (
              <div className="settings-section">
                <h2>General Settings</h2>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Language</h3>
                    <p>Choose your preferred language</p>
                  </div>
                  <select className="setting-control">
                    <option>English</option>
                    <option>Spanish</option>
                    <option>French</option>
                    <option>German</option>
                  </select>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Time Zone</h3>
                    <p>Set your time zone for accurate timestamps</p>
                  </div>
                  <select className="setting-control">
                    <option>UTC</option>
                    <option>EST</option>
                    <option>PST</option>
                    <option>CST</option>
                  </select>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Auto-refresh</h3>
                    <p>Automatically refresh giveaway data</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" defaultChecked />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            )}

            {activeTab === 'appearance' && (
              <div className="settings-section">
                <h2>Appearance</h2>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Theme</h3>
                    <p>Choose your color theme</p>
                  </div>
                  <select 
                    className="setting-control"
                    value={currentTheme}
                    onChange={(e) => onThemeChange(e.target.value)}
                  >
                    <option value="dark">Dark</option>
                    <option value="white">White</option>
                    <option value="blue">Blue</option>
                    <option value="crimson">Crimson</option>
                    <option value="purple">Purple</option>
                    <option value="green">Green</option>
                    <option value="orange">Orange</option>
                    <option value="pink">Pink</option>
                    <option value="cyan">Cyan</option>
                    <option value="indigo">Indigo</option>
                  </select>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Font Size</h3>
                    <p>Adjust the text size</p>
                  </div>
                  <select className="setting-control">
                    <option>Small</option>
                    <option>Medium</option>
                    <option>Large</option>
                    <option>Extra Large</option>
                  </select>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Compact Mode</h3>
                    <p>Use a more compact layout</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Animations</h3>
                    <p>Enable smooth animations</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" defaultChecked />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            )}

            {activeTab === 'notifications' && (
              <div className="settings-section">
                <h2>Notifications</h2>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Email Notifications</h3>
                    <p>Receive notifications via email</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={notifications.email}
                      onChange={() => handleNotificationChange('email')}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Discord Notifications</h3>
                    <p>Receive notifications via Discord</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={notifications.discord}
                      onChange={() => handleNotificationChange('discord')}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Giveaway Updates</h3>
                    <p>Notify when giveaways you entered are updated</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={notifications.giveawayUpdates}
                      onChange={() => handleNotificationChange('giveawayUpdates')}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Announcements</h3>
                    <p>Notify about new announcements</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={notifications.announcements}
                      onChange={() => handleNotificationChange('announcements')}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Messages</h3>
                    <p>Notify when you receive new messages</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={notifications.messages}
                      onChange={() => handleNotificationChange('messages')}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="settings-section">
                <h2>Privacy Settings</h2>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Show Profile</h3>
                    <p>Allow others to view your profile</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={privacy.showProfile}
                      onChange={() => handlePrivacyChange('showProfile')}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Show Entries</h3>
                    <p>Show your entry count on your profile</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={privacy.showEntries}
                      onChange={() => handlePrivacyChange('showEntries')}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Allow Messages</h3>
                    <p>Allow others to send you messages</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={privacy.allowMessages}
                      onChange={() => handlePrivacyChange('allowMessages')}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Show Online Status</h3>
                    <p>Show when you're online in chat</p>
                  </div>
                  <label className="toggle-switch">
                    <input 
                      type="checkbox" 
                      checked={privacy.showOnlineStatus}
                      onChange={() => handlePrivacyChange('showOnlineStatus')}
                    />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            )}

            {activeTab === 'account' && (
              <div className="settings-section">
                <h2>Account Settings</h2>
                {currentUser && (
                  <>
                    <div className="setting-item">
                      <div className="setting-info">
                        <h3>Discord Username</h3>
                        <p>{currentUser.discordUsername}</p>
                      </div>
                      <button className="setting-btn">Edit</button>
                    </div>
                    <div className="setting-item">
                      <div className="setting-info">
                        <h3>Email</h3>
                        <p>{currentUser.email}</p>
                      </div>
                      <button className="setting-btn">Change</button>
                    </div>
                    <div className="setting-item">
                      <div className="setting-info">
                        <h3>Password</h3>
                        <p>Last changed: Never</p>
                      </div>
                      <button className="setting-btn">Change Password</button>
                    </div>
                    <div className="setting-item">
                      <div className="setting-info">
                        <h3>Account Role</h3>
                        <p>{currentUser.role}</p>
                      </div>
                    </div>
                    <div className="setting-item">
                      <div className="setting-info">
                        <h3>Account Created</h3>
                        <p>{new Date(currentUser.createdAt).toLocaleDateString()}</p>
                      </div>
                    </div>
                  </>
                )}
                <div className="danger-zone">
                  <h3>Danger Zone</h3>
                  <button className="danger-btn">Delete Account</button>
                </div>
              </div>
            )}

            {activeTab === 'advanced' && (
              <div className="settings-section">
                <h2>Advanced Settings</h2>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>API Access</h3>
                    <p>Generate API keys for third-party integrations</p>
                  </div>
                  <button className="setting-btn">Manage API Keys</button>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Data Export</h3>
                    <p>Download all your data</p>
                  </div>
                  <button className="setting-btn">Export Data</button>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Clear Cache</h3>
                    <p>Clear cached data and refresh</p>
                  </div>
                  <button className="setting-btn">Clear Cache</button>
                </div>
                <div className="setting-item">
                  <div className="setting-info">
                    <h3>Debug Mode</h3>
                    <p>Enable debug logging</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" />
                    <span className="slider"></span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Settings;

